import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { QRCodeGenerator } from 'src/shared/components';
import {
  GameHeader,
  ExampleImagesSection,
  TextFieldsSection,
  ImagesSection,
  MusicSection,
  ActionsSection,
  ResultsSection,
  processFormData,
  GameExpiredOverlay,
  FileDownloadSection,
} from 'src/shared/components/Constructor';
import { FormValues, TextFieldConfig, ImageFieldConfig } from 'src/shared/components/Constructor/types';

import InfoIcon from '@mui/icons-material/Info';
import { Container, Box, LinearProgress, Typography, Grid, IconButton, Tooltip } from '@mui/material';

import { RealProgress } from '@shared/components/Constructor/GameCreationProgress';
import MusicUploader from '@shared/components/MusicUploader';
import WaveformTrimmer from '@shared/components/WaveformTrimmer';
import type { GameKey } from '@shared/types';
import { isGameExpired } from '@shared/utils/gameExpiration';
import { audioBufferToWav } from '@shared/utils/audio-utils';

import { authContext } from '@store/index';

import * as ClientMinerActions from './client-miner.actions';
import { getIsClientFormDisabled } from './client-miner.utils';

import styles from './client-miner.module.scss';

// Конфигурация полей
const textFields: TextFieldConfig[] = [
  { name: 'customTitle', label: 'clientMiner.customTitle', placeholder: 'clientMiner.customTitlePlaceholder', tooltip: 'clientMiner.customTitleTooltip' },
  { name: 'loseText', label: 'clientMiner.loseText', gridProps: { xs: 12, md: 6 }, tooltip: 'clientMiner.loseTextTooltip' },
  { name: 'winText', label: 'clientMiner.winText', placeholder: 'clientMiner.winTextPlaceholder', gridProps: { xs: 12, md: 6 }, tooltip: 'clientMiner.winTextTooltip' },
];

const imageFields: ImageFieldConfig[] = [
  { name: 'startGameImage', label: 'clientMiner.startGameImage', aspectRatio: 1, tooltip: 'clientMiner.startGameImageTooltip' },
  { name: 'endGameImage', label: 'clientMiner.endGameImage', aspectRatio: 1, tooltip: 'clientMiner.endGameImageTooltip' },
];

interface ClientMinerPageProps {
  gameKey: GameKey;
}

const ClientMinerPage: FC<ClientMinerPageProps> = ({ gameKey: initialGameKey }) => {
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  }, []);

  const [authData] = useContext(authContext);
  const gameDefaults = ClientMinerActions.useFetchGameDefaults(initialGameKey.gameName);

  const [gameKey, setGameKey] = useState(initialGameKey);
  const [gameBuildId, setGameBuildId] = useState(gameKey.gameBuildId);
  // console.log('🔍 ClientMiner: gameBuildId:', gameBuildId);
  const [existingGameBuild, fetchGameBuild, gameBuildError] = ClientMinerActions.useFetchGameBuild(gameBuildId);
  const [createdGameBuild, handleGameBuildSubmit, gameBuildCreationLoading] =
    ClientMinerActions.useCreateGameBuild();
  const [_, setActivateGameDate] = ClientMinerActions.useSetActivateGameDate();
  const [trimmedAudioFile, setTrimmedAudioFile] = useState<File | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [stepProgress, setStepProgress] = useState(0);

  const gameBuild = existingGameBuild ?? createdGameBuild ?? (gameKey as any).gameBuild;
  const showGameLink = Boolean(gameBuild && gameBuild._id);

  const [isGameExpiredState, setIsGameExpiredState] = useState(false);

  useEffect(() => {
    const expired = isGameExpired(gameKey.activateDate, gameKey.gameDayCount);
    setIsGameExpiredState(expired);
  }, [gameKey.activateDate, gameKey.gameDayCount]);

  const isReadOnly = authData?.isReadOnly || false;

  const { t } = useTranslation();

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      ...textFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
      ...imageFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
      customMusicFile: '',
      urlButton: '',
      fileDownloadUrl: '',
    },
    mode: 'onSubmit',
  });

  const cropDataRef = useRef<{ [key: string]: any }>({});


  useEffect(() => {
    // console.log('🔍 ClientMiner: gameBuild changed:', gameBuild);
    if (gameBuild) {
      const newValues: FormValues = {};
      textFields.forEach(field => {
          const keyMap: Record<string, keyof typeof gameBuild> = {
              customTitle: 'nameGameHeader',
              loseText: 'startGameHeader',
              winText: 'endGameHeader',
              winButtonUrl: 'urlButton',
          };
          const buildKey = keyMap[field.name];
          if (buildKey) {
            const value = gameBuild[buildKey] || '';
            newValues[field.name] = value;
            // console.log(`🔍 ClientMiner: ${field.name} -> ${String(buildKey)} = "${value}"`);
          }
      });
      imageFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
            startGameImage: 'startGameImageUrl',
            endGameImage: 'endGameImageUrl',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
            newValues[field.name] = gameBuild[buildKey] || '';
        }
      });
      newValues.customMusicFile = gameBuild.musicUrl || gameDefaults?.defaultMusicUrl || '';
      // Разделяем: urlButton для URL, fileDownloadUrl для файла
      // Используем пустые строки вместо null для избежания предупреждений React
      newValues.urlButton = gameBuild.urlButton || '';
      newValues.fileDownloadUrl = gameBuild.fileDownloadUrl || '';
      console.log('🔍 ClientMiner: Resetting form with values:', newValues);
      console.log('🔍 ClientMiner: fileDownload from gameBuild:', gameBuild.fileDownloadUrl);
      reset(newValues);
    } else if (gameDefaults) {
      const newValues: FormValues = {
        ...textFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
        ...imageFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
        customMusicFile: gameDefaults.defaultMusicUrl || '',
        urlButton: '',
        fileDownloadUrl: '',
      };

      // Устанавливаем значения по умолчанию для изображений из JSON данных
      if (gameDefaults.defaultImages) {
        newValues.startGameImage = gameDefaults.defaultImages.startGameImageUrl || '';
        newValues.endGameImage = gameDefaults.defaultImages.endGameImageUrl || '';
      }

      reset(newValues);
    }
  }, [gameBuild, reset, gameDefaults, createdGameBuild, existingGameBuild]);

  const onSubmit = async (data: FormValues) => {
    try {
      // Показываем прогресс создания игры
      // console.log('Starting progress...');
      setShowProgress(true);
      setCurrentStep('validation');
      setStepProgress(0);
      
      // Автоматическая обрезка и компрессия музыки, если она новая
      let audioFileToUse = trimmedAudioFile || data.customMusicFile;
      
      // Если есть музыкальный файл и он новый (не обрезанный), запускаем автоматическую обрезку
      if (data.customMusicFile instanceof File && !trimmedAudioFile) {
        setCurrentStep('music');
        setStepProgress(10);
        // console.log('Запускаем автоматическую обрезку и компрессию музыки...');
        
        try {
          // Создаем AudioContext для обрезки
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          
          // Читаем файл
          const arrayBuffer = await data.customMusicFile.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // console.log('Аудио декодировано, длительность:', audioBuffer.duration, 'сек');
          
          // Используем полную длительность аудио без автоматической обрезки
          const startTime = 0;
          const endTime = audioBuffer.duration;
          
          // console.log('Используем полную длительность аудио:', { startTime, endTime, duration: audioBuffer.duration });
          
          // Вычисляем сэмплы для обрезки
          const sampleRate = audioBuffer.sampleRate;
          const startSample = Math.floor(startTime * sampleRate);
          const endSample = Math.floor(endTime * sampleRate);
          const trimmedLength = endSample - startSample;
          
          // Создаем новый AudioBuffer для обрезанного аудио
          const trimmedBuffer = audioContext.createBuffer(
            audioBuffer.numberOfChannels,
            trimmedLength,
            sampleRate
          );
          
          // Копируем данные из выбранного диапазона
          for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            const originalData = audioBuffer.getChannelData(channel);
            const trimmedData = trimmedBuffer.getChannelData(channel);
            
            for (let i = 0; i < trimmedLength; i++) {
              trimmedData[i] = originalData[startSample + i];
            }
          }
          
          // Конвертируем AudioBuffer в MP3 используя MP3Compressor
          const { MP3Compressor } = await import('@shared/utils/mp3-compression');
          const compressor = new MP3Compressor();
          
          // Сохраняем оригинальное название файла
          const originalFileName = data.customMusicFile.name;
          const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
          
          // Создаем WAV файл из AudioBuffer
          const wavBlob = await audioBufferToWav(trimmedBuffer);
          const tempFile = new File([wavBlob], `${fileNameWithoutExt}`, { type: 'audio/wav' });
          
          const compressionResult = await compressor.compressMP3(tempFile);
          const trimmedFile = compressionResult.compressedFile;
          
          // console.log('Автоматическая обрезка завершена:', trimmedFile.name, 'размер:', trimmedFile.size);
          
          audioFileToUse = trimmedFile;
          // Проверяем состояние AudioContext перед закрытием
          if (audioContext.state !== 'closed') {
            try {
              audioContext.close();
            } catch (error) {
              // console.error('Ошибка при закрытии AudioContext:', error);
            }
          }
          
                 } catch (error) {
           console.error('Ошибка автоматической обрезки музыки:', error);
           // Если обрезка не удалась, используем оригинальный файл
           audioFileToUse = data.customMusicFile;
         }
      }
      
      setCurrentStep('images');
      setStepProgress(30);
      
      console.log('🔍 ClientMiner onSubmit data:', data);
      console.log('🔍 ClientMiner fileDownload value:', data.fileDownload);
      
      const formData = await processFormData(
        { ...data, customMusicFile: audioFileToUse },
        gameKey,
        trimmedAudioFile,
        {
          imageFields: imageFields.map(f => f.name),
          musicFields: ['customMusicFile'],
          fileFields: ['urlButton'],
        }
      );
      
      setCurrentStep('upload');
      setStepProgress(60);
      
      await handleGameBuildSubmit(formData);
      
      setCurrentStep('creation');
      setStepProgress(80);
      
      // Имитируем завершение создания
      setTimeout(() => {
        setCurrentStep('complete');
        setStepProgress(100);
      }, 1000);
      
      // Скрываем прогресс после завершения
      setTimeout(() => {
        setShowProgress(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('root', { message: 'Ошибка при сохранении игры' });
      setShowProgress(false);
      setCurrentStep('');
      setStepProgress(0);
    }
  };
  
  const onError = (errors: any) => {
    const missingFields = [
        ...textFields.filter(f => f.required && errors[f.name]),
    ];

    if (missingFields.length > 0) {
      const fieldsList = missingFields.map(({ label }) => `📝 ${t(label)}`).join('\n');
      const errorMessage = `${t('client.validation.requiredFieldsMessage')}\n\n${fieldsList}`;
      setError('root', { 
        message: errorMessage
      });
    }
  };

  useEffect(() => {
    if (createdGameBuild) {
      const result = createdGameBuild;
      if (result && result._id) {
        setGameKey((prev) => ({
          ...prev,
          gameBuild: result,
          gameBuildId: result._id,
        }));
        setGameBuildId(result._id);
        
        // Принудительно обновляем данные с сервера
        // fetchGameBuild автоматически обновится при изменении gameBuildId
        
        // Принудительно обновляем форму с новыми данными
        const newValues: FormValues = {};
        textFields.forEach(field => {
            const keyMap: Record<string, keyof typeof result> = {
                customTitle: 'nameGameHeader',
                loseText: 'startGameHeader',
                winText: 'endGameHeader',
                winButtonUrl: 'urlButton',
            };
            const buildKey = keyMap[field.name];
            if (buildKey) {
              newValues[field.name] = result[buildKey] || '';
            }
        });
        imageFields.forEach(field => {
          const keyMap: Record<string, keyof typeof result> = {
              startGameImage: 'startGameImageUrl',
              endGameImage: 'endGameImageUrl',
          };
          const buildKey = keyMap[field.name];
          if (buildKey) {
              newValues[field.name] = result[buildKey] || '';
          }
        });
        newValues.customMusicFile = result.musicUrl || gameDefaults?.defaultMusicUrl || '';
        // Используем пустые строки вместо null для избежания предупреждений React
        newValues.urlButton = result.urlButton || '';
        newValues.fileDownloadUrl = result.fileDownloadUrl || '';
        console.log('🔍 ClientMiner: After creation - fileDownload from result:', result.fileDownloadUrl);
        reset(newValues);
      }

      if (!gameKey.activateDate) {
        setActivateGameDate(gameKey._id);
        setGameKey((prev) => ({
          ...prev,
          activateDate: new Date(),
        }));
      }
    }
  }, [createdGameBuild, setActivateGameDate, reset, gameDefaults]);

  const handleTestGameClick = () => {
    if (gameBuild) {
      window.open(`/game?buildId=${gameBuild._id}`, '_blank');
    }
  };

  const { activateDate, editDayCount, gameDayCount } = gameKey;

  const isClientFormDisabled = getIsClientFormDisabled(
    activateDate ? (typeof activateDate === 'string' ? new Date(activateDate) : activateDate) : null,
    editDayCount
  ) || isReadOnly || isGameExpiredState;

  return (
    <Container maxWidth="md" className={styles.gameContainer}>
      <GameHeader
        gameDefaults={gameDefaults}
        activateDate={activateDate ? (typeof activateDate === 'string' ? new Date(activateDate) : activateDate) : null}
        editDayCount={editDayCount}
        gameDayCount={gameDayCount}
      />

      <ExampleImagesSection gameDefaults={gameDefaults} />

      {/* Компонент прогресса создания игры */}
      {showProgress && (
        <RealProgress
          isVisible={showProgress}
          onComplete={() => {
            setShowProgress(false);
            setCurrentStep('');
            setStepProgress(0);
          }}
          currentStep={currentStep}
          stepProgress={stepProgress}
        />
      )}

      <form key={gameBuild?._id || 'default'} onSubmit={handleSubmit(onSubmit, onError)}>
        <TextFieldsSection control={control} errors={errors} fields={textFields} />

        <ImagesSection 
          control={control} 
          errors={errors} 
          isReadOnly={isReadOnly} 
          cropDataRef={cropDataRef} 
          images={imageFields}
          gameType="miner"
        />

        <Box sx={{ mt: 4 }}>
          <Box className={styles.formSection}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
              <Typography variant="h5" className={styles.sectionTitle}>{t('clientMiner.musicSectionTitle')}</Typography>
              <Tooltip title={t('clientAll.musicUploadTooltip')} arrow placement="top">
                <IconButton size="small" sx={{ display: 'flex', alignItems: 'center' }}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="customMusicFile"
                  control={control}
                  render={({ field }) => {
                    return (
                      <MusicUploader
                        value={field.value ?? ''}
                        isDisabled={isReadOnly}
                        onChange={(file) => {
                          field.onChange(file);
                        }}
                        setError={(msg) => {
                          setError('customMusicFile', { message: msg });
                        }}
                      />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                {(() => {
                  const customMusicFile = watch('customMusicFile');
                  const getFileName = (value: File | string | undefined) => {
                    if (!value) return '';
                    if (typeof value === 'string') {
                      return value.split('/').pop() || value;
                    }
                    return value.name;
                  };
                  
                  return (
                    <WaveformTrimmer
                      audioFile={customMusicFile instanceof File ? customMusicFile : (typeof customMusicFile === 'string' && customMusicFile ? customMusicFile : null)}
                      fileName={getFileName(customMusicFile)}
                      disabled={isReadOnly}
                      onTrimChange={(startTime: number, endTime: number) => {
                        console.log('Выбранный отрезок:', { startTime, endTime });
                      }}
                      onTrimmedFileChange={setTrimmedAudioFile}
                    />
                  );
                })()}
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Box className={styles.formSection}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FileDownloadSection
                  control={control}
                  errors={errors}
                  isReadOnly={isReadOnly}
                  setError={setError}
                  setValue={setValue}
                  getValues={getValues}
                  fieldName="urlButton"
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <ActionsSection
          gameBuild={gameBuild}
          gameBuildCreationLoading={gameBuildCreationLoading}
          isReadOnly={isReadOnly}
          onTestGameClick={handleTestGameClick}
          hasErrors={!!errors.root || Object.keys(errors).length > 0}
        />

        <ResultsSection
          errors={errors}
          gameBuild={gameBuild}
          showGameLink={showGameLink}
        />

        {/* QR-код компонент */}
        <QRCodeGenerator
          gameUrl={gameBuild ? `${window.location.origin}/game?buildId=${gameBuild._id}` : ''}
          gameBuildId={gameBuild?._id}
          showGameLink={showGameLink}
        />
      </form>

      {/* Оверлей с уведомлением об истечении срока */}
      <GameExpiredOverlay isVisible={isGameExpiredState} />
    </Container>
  );
};

export default ClientMinerPage; 